"use client";

import { useEffect, useRef, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useTranslations } from "next-intl";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Description, DialogTitle } from "@radix-ui/react-dialog";
import { Typography } from "@/components/custom/typography";
import { useTermOfService } from "@/hooks/sign-up-hooks/useTermOfService";

export function TermOfService({
  open,
  onClose,
  onAgree,
  defaultChecked = false,
  onRequestCancel,
}: {
  open: boolean;
  onClose: () => void;
  onAgree: () => void;
  defaultChecked?: boolean;
  onRequestCancel?: () => void;
}) {
  type FormValues = {
    agree: boolean;
  };

  const { control, handleSubmit, reset, setValue } = useForm<FormValues>({
    defaultValues: {
      agree: defaultChecked,
    },
  });

  const isChecked = useWatch({
    control,
    name: "agree",
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(defaultChecked);

  useEffect(() => {
    if (open) {
      reset({ agree: defaultChecked });
      setScrolledToBottom(defaultChecked);
    }
  }, [open, defaultChecked, reset]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const isBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;

    if (isBottom && !scrolledToBottom) {
      setScrolledToBottom(true);
      setValue("agree", true);
    }
  };

  const { data } = useTermOfService();
  const t = useTranslations("dialogs.termOfService");
  const advertisingPolicy = data?.find(
    (item) => item.policy_type === "advertising",
  );

  if (!advertisingPolicy) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-4xl p-10 [&>button]:hidden flex flex-col gap-9 rounded-xl">
        {/* Title */}
        <div>
          <DialogTitle></DialogTitle>
          <Typography variant="subheading1" tone="strong" className="mb-3">
            {t("title")}
          </Typography>
          <Description>
            <Typography
              as="span"
              variant="body2"
              className="whitespace-pre-line"
            >
              {t("description")}
            </Typography>
          </Description>
        </div>

        {/* Body */}
        <div className="space-y-5">
          <div className="flex flex-col gap-3">
            <Typography variant="body2" tone="medium" className="font-semibold">
              {t("sectionTitle")}
            </Typography>
            <Typography variant="body3" tone="lightnormal">
              {t("sectionDescription")}
            </Typography>
          </div>

          {/* Scroll content */}
          <ScrollArea className="h-64" onScroll={handleScroll}>
            <div
              ref={scrollRef}
              className="h-full overflow-y-auto p-2.5 rounded bg-gray-50 border border-gray-200 whitespace-pre-line"
            >
              <Typography as="div" variant="body4" className="text-black">
                {advertisingPolicy?.policy_content ??
                  "약관 내용을 불러오는 중입니다..."}
              </Typography>
            </div>
          </ScrollArea>

          {/* Checkbox */}
          <div className="flex items-center p-1 gap-1 w-full bg-muted rounded-xs">
            <Controller
              control={control}
              name="agree"
              render={({ field }) => (
                <Checkbox
                  id="agree"
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                  }}
                  disabled={!scrolledToBottom}
                />
              )}
            />
            <Typography variant="body3">{t("checkboxText")}</Typography>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3.5 justify-end  ">
          <Button
            type="button"
            variant="secondary"
            tone={"light"}
            className="border border-gray-300 font-semibold text-base"
            onClick={() => {
              if (onRequestCancel) onRequestCancel();
              else onClose();
            }}
          >
            {t("cancelText")}
          </Button>

          <Button
            type="submit"
            className="font-semibold text-base"
            onClick={handleSubmit((data) => {
              if (data.agree) onAgree();
              onClose();
            })}
            disabled={!isChecked}
          >
            {t("okText")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
